import { authedFetch, getCsrfToken } from "@/lib/authed-fetch";
import { useState, useMemo, useRef, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { UploadProgressDialog } from "./upload-progress-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  AlertTriangle,
  Loader2,
  Calendar,
  User,
  Paperclip,
  X,
  ChevronDown,
  ChevronRight,
  FolderPlus,
  Pencil,
} from "lucide-react";
import type { ClientResource, ClientResourceAttachment } from "@shared/schema";

type ResourceWithAttachments = ClientResource & { attachments: ClientResourceAttachment[] };

type UploadProgressState = {
  open: boolean;
  files: Array<{
    name: string;
    size: number;
    progress: number;
    status: 'waiting' | 'uploading' | 'completed' | 'error' | 'cancelled';
  }>;
  totalProgress: number;
};

function renderTextWithLinks(text: string) {
  const words = text.split(/(\s+)/);
  return words.map((word, i) => {
    const trimmed = word.trim();
    if (!trimmed) return word;
    try {
      const url = new URL(trimmed.replace(/[.,;:!?)]+$/, ''));
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        const trailingPunct = trimmed.slice(url.href.length);
        return (
          <span key={i}>
            <a
              href={url.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline"
            >
              {url.href}
            </a>
            {trailingPunct}
          </span>
        );
      }
    } catch {}
    return word;
  });
}

function formatDate(dateStr: string | Date) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatFileSize(bytes: number | null) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ClientResourcesProps {
  clientId: string;
}

export function ClientResources({ clientId }: ClientResourcesProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showAdd, setShowAdd] = useState(false);
  const [addCategory, setAddCategory] = useState<string | null>(null);
  const [editing, setEditing] = useState<ResourceWithAttachments | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; title: string; hasFiles: boolean } | null>(null);

  const [newTitle, setNewTitle] = useState("");
  const [newText, setNewText] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadProgress, setUploadProgress] = useState<UploadProgressState>({
    open: false, files: [], totalProgress: 0
  });

  const [showAddProject, setShowAddProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");

  const [renamingCategory, setRenamingCategory] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState<string | null>(null);

  const { data: resources = [], isLoading } = useQuery<ResourceWithAttachments[]>({
    queryKey: [`/api/clients/${clientId}/resources`],
    enabled: !!clientId,
  });

  const { data: categories = [] } = useQuery<string[]>({
    queryKey: [`/api/clients/${clientId}/categories`],
    enabled: !!clientId,
  });

  const { data: allUsers = [] } = useQuery<any[]>({
    queryKey: ['/api/users'],
  });

  const userMap = useMemo(() => {
    const map: Record<number, string> = {};
    for (const u of allUsers) {
      map[u.id] = u.username || u.firstName || `User ${u.id}`;
    }
    return map;
  }, [allUsers]);

  const groupedResources = useMemo(() => {
    const groups = new Map<string | null, ResourceWithAttachments[]>();
    groups.set(null, []);
    const sortedCategories = [...categories].sort((a, b) => a.localeCompare(b));
    for (const cat of sortedCategories) {
      groups.set(cat, []);
    }
    for (const r of resources) {
      const key = r.category || null;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(r);
    }
    return groups;
  }, [resources, categories]);

  const uploadFilesWithProgress = useCallback(async (
    url: string,
    files: File[],
  ): Promise<any[]> => {
    if (files.length === 0) return [];

    const progressFiles = files.map(f => ({
      name: f.name,
      size: f.size,
      progress: 0,
      status: 'waiting' as const,
    }));

    setUploadProgress({ open: true, files: progressFiles, totalProgress: 0 });

    const results: any[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      setUploadProgress(prev => ({
        ...prev,
        files: prev.files.map((f, idx) =>
          idx === i ? { ...f, status: 'uploading' } : f
        ),
      }));

      try {
        const formData = new FormData();
        formData.append('files', file);

        const result = await new Promise<any>((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const progress = Math.round((e.loaded / e.total) * 100);
              setUploadProgress(prev => {
                const newFiles = [...prev.files];
                newFiles[i] = { ...newFiles[i], progress };
                const totalProgress = newFiles.reduce((sum, f) => sum + f.progress, 0) / newFiles.length;
                return { ...prev, files: newFiles, totalProgress };
              });
            }
          });

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(JSON.parse(xhr.responseText));
            } else {
              reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
            }
          };

          xhr.onerror = () => reject(new Error('Network error'));
          xhr.open('POST', url);
          xhr.withCredentials = true;
          const csrfToken = getCsrfToken();
          if (csrfToken) xhr.setRequestHeader('X-CSRF-Token', csrfToken);
          xhr.send(formData);
        });

        results.push(result);

        setUploadProgress(prev => ({
          ...prev,
          files: prev.files.map((f, idx) =>
            idx === i ? { ...f, status: 'completed', progress: 100 } : f
          ),
        }));
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);

        setUploadProgress(prev => ({
          ...prev,
          files: prev.files.map((f, idx) =>
            idx === i ? { ...f, status: 'error' } : f
          ),
        }));

        toast({
          title: `Failed to upload ${file.name}`,
          variant: "destructive",
        });
      }
    }

    setTimeout(() => {
      setUploadProgress({ open: false, files: [], totalProgress: 0 });
    }, 1000);

    return results;
  }, [toast]);

  const handleCreateResource = useCallback(async () => {
    if (!newTitle) return;

    try {
      const createBody = new FormData();
      createBody.append('title', newTitle);
      if (newText) createBody.append('text', newText);
      const categoryValue = newCategory.trim();
      if (categoryValue) createBody.append('category', categoryValue);

      const res = await authedFetch(`/api/clients/${clientId}/resources`, {
        method: 'POST',
        body: createBody,
        credentials: 'include',
      });
      if (!res.ok) throw new Error("Create failed");
      const resource = await res.json();

      if (selectedFiles.length > 0) {
        await uploadFilesWithProgress(
          `/api/clients/${clientId}/resources/${resource.id}/attachments`,
          selectedFiles,
        );
      }

      queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/resources`] });
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/categories`] });
      resetForm();
      toast({ title: "Resource created" });
    } catch {
      toast({ title: "Failed to create resource", variant: "destructive" });
    }
  }, [newTitle, newText, newCategory, selectedFiles, clientId, queryClient, toast, uploadFilesWithProgress]);

  const handleAddAttachments = useCallback(async (resourceId: number, files: File[]) => {
    await uploadFilesWithProgress(
      `/api/clients/${clientId}/resources/${resourceId}/attachments`,
      files,
    );
    queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/resources`] });
    toast({ title: "Files attached" });
  }, [clientId, queryClient, toast, uploadFilesWithProgress]);

  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = useCallback(async () => {
    setIsCreating(true);
    await handleCreateResource();
    setIsCreating(false);
  }, [handleCreateResource]);

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: any }) => {
      const res = await apiRequest('PATCH', `/api/clients/${clientId}/resources/${id}`, updates);
      if (!res.ok) throw new Error("Update failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/resources`] });
      setEditing(null);
      toast({ title: "Resource updated" });
    },
    onError: () => toast({ title: "Failed to update resource", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest('DELETE', `/api/clients/${clientId}/resources/${id}`);
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/resources`] });
      setDeleteTarget(null);
      toast({ title: "Resource deleted" });
    },
    onError: () => toast({ title: "Failed to delete resource", variant: "destructive" }),
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: async ({ resourceId, attachmentId }: { resourceId: number; attachmentId: number }) => {
      const res = await apiRequest('DELETE', `/api/clients/${clientId}/resources/${resourceId}/attachments/${attachmentId}`);
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/resources`] });
      toast({ title: "Attachment removed" });
    },
    onError: () => toast({ title: "Failed to remove attachment", variant: "destructive" }),
  });

  const renameCategoryMutation = useMutation({
    mutationFn: async ({ oldName, newName }: { oldName: string; newName: string }) => {
      const res = await apiRequest('PATCH', `/api/clients/${clientId}/categories`, { oldName, newName });
      if (!res.ok) throw new Error("Rename failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/resources`] });
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/categories`] });
      setRenamingCategory(null);
      setRenameValue("");
      toast({ title: "Project renamed" });
    },
    onError: () => toast({ title: "Failed to rename project", variant: "destructive" }),
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (categoryName: string) => {
      const res = await apiRequest('DELETE', `/api/clients/${clientId}/categories/${encodeURIComponent(categoryName)}`);
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/resources`] });
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/categories`] });
      setDeleteCategoryTarget(null);
      toast({ title: "Project deleted" });
    },
    onError: () => toast({ title: "Failed to delete project", variant: "destructive" }),
  });

  function resetForm() {
    setShowAdd(false);
    setAddCategory(null);
    setNewTitle("");
    setNewText("");
    setNewCategory("");
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function openAddForCategory(category: string | null) {
    setAddCategory(category);
    setNewCategory(category || "");
    setShowAdd(true);
  }

  function toggleUpToDate(id: number, currentIsOutOfDate: boolean) {
    updateMutation.mutate({ id, updates: { isOutOfDate: !currentIsOutOfDate } });
  }

  async function handleDownloadAttachment(attachment: ClientResourceAttachment) {
    try {
      const res = await fetch(`/api/clients/${clientId}/resources/attachments/${attachment.id}/download`, {
        credentials: 'include',
      });
      if (!res.ok) {
        toast({ title: "File not found in storage", variant: "destructive" });
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Download failed", variant: "destructive" });
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeSelectedFile(index: number) {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }

  function handleAddProject() {
    const name = newProjectName.trim();
    if (!name) return;
    setShowAddProject(false);
    setNewProjectName("");
    openAddForCategory(name);
  }

  const allCategoryNames = useMemo(() => {
    const names = new Set<string>();
    for (const cat of categories) names.add(cat);
    for (const r of resources) {
      if (r.category) names.add(r.category);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [categories, resources]);

  const orderedGroups = useMemo(() => {
    const result: Array<{ key: string | null; label: string; resources: ResourceWithAttachments[] }> = [];
    result.push({
      key: null,
      label: "Client Resources",
      resources: groupedResources.get(null) || [],
    });
    const customKeys = Array.from(groupedResources.keys())
      .filter((k): k is string => k !== null)
      .sort((a, b) => a.localeCompare(b));
    for (const key of customKeys) {
      result.push({
        key,
        label: key,
        resources: groupedResources.get(key) || [],
      });
    }
    return result;
  }, [groupedResources]);

  return (
    <div className="space-y-2">
      {isLoading ? (
        <div className="text-xs text-gray-400 py-4 text-center flex items-center justify-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading...
        </div>
      ) : (
        <div className="space-y-1">
          {orderedGroups.map(group => (
            <CategorySection
              key={group.key ?? '__default__'}
              categoryKey={group.key}
              label={group.label}
              resources={group.resources}
              clientId={clientId}
              onAdd={() => openAddForCategory(group.key)}
              onDownloadAttachment={handleDownloadAttachment}
              onEdit={setEditing}
              onDelete={(r) => setDeleteTarget({ id: r.id, title: r.title, hasFiles: r.attachments.length > 0 })}
              onToggle={toggleUpToDate}
              onAddFiles={handleAddAttachments}
              onDeleteAttachment={(resourceId, attachmentId) => deleteAttachmentMutation.mutate({ resourceId, attachmentId })}
              userMap={userMap}
              onRenameCategory={(name) => {
                setRenamingCategory(name);
                setRenameValue(name);
              }}
              onDeleteCategory={(name) => setDeleteCategoryTarget(name)}
            />
          ))}
        </div>
      )}

      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs w-full border-dashed border-gray-600 text-gray-400 hover:text-gray-200"
        onClick={() => setShowAddProject(true)}
      >
        <FolderPlus className="h-3 w-3 mr-1" />
        Add Project
      </Button>

      <Dialog open={showAdd} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Add Resource</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Project / Category</Label>
              <Input
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Leave empty for default group"
                className="h-8 text-xs"
                list="category-suggestions"
              />
              <datalist id="category-suggestions">
                {allCategoryNames.map(name => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </div>
            <div>
              <Label className="text-xs">Title</Label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Resource title"
                className="h-8 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="Notes, instructions, links... URLs will be auto-linked."
                className="text-xs min-h-[80px]"
              />
            </div>
            <div>
              <Label className="text-xs">Attach Files (optional)</Label>
              <Input
                ref={fileInputRef}
                type="file"
                multiple
                className="h-8 text-xs"
                onChange={handleFileSelect}
              />
              {selectedFiles.length > 0 && (
                <div className="mt-2 space-y-1">
                  {selectedFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-[10px] text-gray-300 bg-gray-800/60 rounded px-2 py-1">
                      <Paperclip className="h-2.5 w-2.5 text-blue-400 shrink-0" />
                      <span className="truncate flex-1">{file.name}</span>
                      <span className="text-gray-500 shrink-0">{formatFileSize(file.size)}</span>
                      <button onClick={() => removeSelectedFile(idx)} className="text-gray-400 hover:text-red-400 shrink-0">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={resetForm} className="text-xs h-7">Cancel</Button>
            <Button
              size="sm"
              className="text-xs h-7"
              disabled={!newTitle || isCreating}
              onClick={handleCreate}
            >
              {isCreating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddProject} onOpenChange={(open) => { if (!open) { setShowAddProject(false); setNewProjectName(""); } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Add Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Project Name</Label>
              <Input
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Enter project name"
                className="h-8 text-xs"
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddProject(); }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => { setShowAddProject(false); setNewProjectName(""); }} className="text-xs h-7">Cancel</Button>
            <Button
              size="sm"
              className="text-xs h-7"
              disabled={!newProjectName.trim()}
              onClick={handleAddProject}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editing && (
        <Dialog open={true} onOpenChange={() => setEditing(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-sm">Edit Resource</DialogTitle>
            </DialogHeader>
            <EditResourceForm
              resource={editing}
              clientId={clientId}
              onSave={(updates) => updateMutation.mutate({ id: editing.id, updates })}
              onCancel={() => setEditing(null)}
              onAddFiles={(files) => handleAddAttachments(editing.id, files)}
              onDeleteAttachment={(attachmentId) => deleteAttachmentMutation.mutate({ resourceId: editing.id, attachmentId })}
              onDownloadAttachment={handleDownloadAttachment}
              isPending={updateMutation.isPending}
            />
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Confirm Deletion
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs">
              Are you sure you want to delete "{deleteTarget?.title}"?
              {deleteTarget?.hasFiles && " This will permanently remove all attached files from storage."}
              {" This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs h-7">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 text-xs h-7"
              onClick={() => {
                if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!renamingCategory} onOpenChange={(open) => { if (!open) { setRenamingCategory(null); setRenameValue(""); } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Rename Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">New Name</Label>
              <Input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="Project name"
                className="h-8 text-xs"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && renameValue.trim() && renamingCategory) {
                    renameCategoryMutation.mutate({ oldName: renamingCategory, newName: renameValue.trim() });
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => { setRenamingCategory(null); setRenameValue(""); }} className="text-xs h-7">Cancel</Button>
            <Button
              size="sm"
              className="text-xs h-7"
              disabled={!renameValue.trim() || renameValue.trim() === renamingCategory || renameCategoryMutation.isPending}
              onClick={() => {
                if (renamingCategory) {
                  renameCategoryMutation.mutate({ oldName: renamingCategory, newName: renameValue.trim() });
                }
              }}
            >
              {renameCategoryMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteCategoryTarget} onOpenChange={(open) => { if (!open) setDeleteCategoryTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Delete Project
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs">
              Are you sure you want to delete the project "{deleteCategoryTarget}"?
              This will permanently delete all resources and their attachments in this project.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs h-7">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 text-xs h-7"
              onClick={() => {
                if (deleteCategoryTarget) deleteCategoryMutation.mutate(deleteCategoryTarget);
              }}
            >
              Delete Project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UploadProgressDialog
        open={uploadProgress.open}
        files={uploadProgress.files}
        totalProgress={uploadProgress.totalProgress}
      />
    </div>
  );
}

function CategorySection({
  categoryKey,
  label,
  resources,
  clientId,
  onAdd,
  onDownloadAttachment,
  onEdit,
  onDelete,
  onToggle,
  onAddFiles,
  onDeleteAttachment,
  userMap,
  onRenameCategory,
  onDeleteCategory,
}: {
  categoryKey: string | null;
  label: string;
  resources: ResourceWithAttachments[];
  clientId: string;
  onAdd: () => void;
  onDownloadAttachment: (att: ClientResourceAttachment) => void;
  onEdit: (r: ResourceWithAttachments) => void;
  onDelete: (r: ResourceWithAttachments) => void;
  onToggle: (id: number, currentIsOutOfDate: boolean) => void;
  onAddFiles: (resourceId: number, files: File[]) => void;
  onDeleteAttachment: (resourceId: number, attachmentId: number) => void;
  userMap: Record<number, string>;
  onRenameCategory: (name: string) => void;
  onDeleteCategory: (name: string) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const isCustom = categoryKey !== null;
  const activeResources = useMemo(() => resources.filter(r => !r.isOutOfDate), [resources]);
  const outdatedResources = useMemo(() => resources.filter(r => r.isOutOfDate), [resources]);

  return (
    <div className="border border-gray-700/50 rounded bg-gray-900/30">
      <div
        className="flex items-center justify-between px-2 py-1.5 cursor-pointer hover:bg-gray-800/40 rounded-t"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          {collapsed ? (
            <ChevronRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-gray-400 shrink-0" />
          )}
          <span className="text-xs font-medium text-gray-300 truncate">{label}</span>
          <span className="text-[10px] text-gray-500">({resources.length})</span>
        </div>
        <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
          {isCustom && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={() => onRenameCategory(categoryKey)}
                title="Rename project"
              >
                <Pencil className="h-2.5 w-2.5 text-gray-400" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 text-destructive hover:text-destructive"
                onClick={() => onDeleteCategory(categoryKey)}
                title="Delete project"
              >
                <Trash2 className="h-2.5 w-2.5" />
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-5 text-[10px] px-1.5"
            onClick={onAdd}
          >
            <Plus className="h-2.5 w-2.5 mr-0.5" />
            Add
          </Button>
        </div>
      </div>

      {!collapsed && (
        <div className="px-2 pb-2 space-y-1">
          {resources.length === 0 ? (
            <div className="text-[10px] text-gray-500 py-3 text-center border border-dashed border-gray-700 rounded">
              No resources yet
            </div>
          ) : (
            <>
              {activeResources.map(resource => (
                <ResourceRow
                  key={resource.id}
                  resource={resource}
                  clientId={clientId}
                  onDownloadAttachment={onDownloadAttachment}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onToggle={onToggle}
                  onAddFiles={onAddFiles}
                  onDeleteAttachment={onDeleteAttachment}
                  userMap={userMap}
                />
              ))}
              {outdatedResources.length > 0 && (
                <>
                  {activeResources.length > 0 && <div className="border-t border-gray-700 my-2" />}
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider px-1 mb-1">Out of Date</div>
                  {outdatedResources.map(resource => (
                    <ResourceRow
                      key={resource.id}
                      resource={resource}
                      clientId={clientId}
                      onDownloadAttachment={onDownloadAttachment}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onToggle={onToggle}
                      onAddFiles={onAddFiles}
                      onDeleteAttachment={onDeleteAttachment}
                      userMap={userMap}
                    />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ResourceRow({ resource, clientId, onDownloadAttachment, onEdit, onDelete, onToggle, onAddFiles, onDeleteAttachment, userMap }: {
  resource: ResourceWithAttachments;
  clientId: string;
  onDownloadAttachment: (att: ClientResourceAttachment) => void;
  onEdit: (r: ResourceWithAttachments) => void;
  onDelete: (r: ResourceWithAttachments) => void;
  onToggle: (id: number, currentIsOutOfDate: boolean) => void;
  onAddFiles: (resourceId: number, files: File[]) => void;
  onDeleteAttachment: (resourceId: number, attachmentId: number) => void;
  userMap: Record<number, string>;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const creatorName = resource.createdBy ? userMap[resource.createdBy] : null;
  const hasText = !!resource.text;
  const attachmentCount = resource.attachments.length;

  return (
    <div className={`p-2 rounded bg-gray-800/40 border border-gray-700/50 ${resource.isOutOfDate ? 'opacity-50' : ''}`}>
      <div className="flex items-start gap-2">
        <FileText className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className={`text-xs font-medium ${resource.isOutOfDate ? 'line-through text-gray-400' : 'text-white'}`}>
            {resource.title}
          </div>
          {hasText && (
            <div className="text-xs text-gray-300 mt-1 whitespace-pre-wrap break-words">
              {renderTextWithLinks(resource.text!)}
            </div>
          )}
          {attachmentCount > 0 && (
            <div className="mt-1 space-y-0.5">
              {resource.attachments.map(att => (
                <div key={att.id} className="flex items-center gap-1 text-[10px] text-blue-400/70 group">
                  <Paperclip className="h-2.5 w-2.5 shrink-0" />
                  <button
                    onClick={() => onDownloadAttachment(att)}
                    className="truncate hover:text-blue-300 hover:underline text-left"
                    title={`Download ${att.filename}`}
                  >
                    {att.filename}
                  </button>
                  {att.fileSize && (
                    <span className="text-gray-500 shrink-0">({formatFileSize(att.fileSize)})</span>
                  )}
                  <button
                    onClick={() => onDeleteAttachment(resource.id, att.id)}
                    className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 shrink-0 ml-auto"
                    title="Remove attachment"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-2">
            <span className="flex items-center gap-0.5">
              <Calendar className="h-2.5 w-2.5" />
              {formatDate(resource.createdAt)}
            </span>
            {creatorName && (
              <>
                <span className="text-gray-600">•</span>
                <span className="flex items-center gap-0.5">
                  <User className="h-2.5 w-2.5" />
                  {creatorName}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <div className="flex items-center gap-1 mr-1" title={resource.isOutOfDate ? 'Currently out of date — toggle to mark as up to date' : 'Currently up to date — toggle to mark as out of date'}>
            <span className={`text-[9px] ${resource.isOutOfDate ? 'text-amber-500' : 'text-green-500'}`}>
              {resource.isOutOfDate ? 'OOD' : 'UTD'}
            </span>
            <Switch
              checked={!resource.isOutOfDate}
              onCheckedChange={() => onToggle(resource.id, resource.isOutOfDate)}
              className="scale-75"
            />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length > 0) onAddFiles(resource.id, files);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => fileInputRef.current?.click()} title="Attach files">
            <Paperclip className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onEdit(resource)} title="Edit">
            <Edit className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive" onClick={() => onDelete(resource)} title="Delete">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditResourceForm({ resource, clientId, onSave, onCancel, onAddFiles, onDeleteAttachment, onDownloadAttachment, isPending }: {
  resource: ResourceWithAttachments;
  clientId: string;
  onSave: (updates: { title?: string; text?: string | null }) => void;
  onCancel: () => void;
  onAddFiles: (files: File[]) => void;
  onDeleteAttachment: (attachmentId: number) => void;
  onDownloadAttachment: (att: ClientResourceAttachment) => void;
  isPending: boolean;
}) {
  const [title, setTitle] = useState(resource.title);
  const [text, setText] = useState(resource.text || "");
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-8 text-xs" />
        </div>
        <div>
          <Label className="text-xs">Notes</Label>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} className="text-xs min-h-[80px]" placeholder="Notes, instructions, links..." />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs">Attachments ({resource.attachments.length})</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 text-[10px] px-2"
              onClick={() => fileInputRef.current?.click()}
            >
              <Plus className="h-2.5 w-2.5 mr-1" />
              Add Files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) onAddFiles(files);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            />
          </div>
          {resource.attachments.length > 0 ? (
            <div className="space-y-1">
              {resource.attachments.map(att => (
                <div key={att.id} className="flex items-center gap-2 text-[10px] bg-gray-800/60 rounded px-2 py-1 group">
                  <Paperclip className="h-2.5 w-2.5 text-blue-400 shrink-0" />
                  <button
                    onClick={() => onDownloadAttachment(att)}
                    className="truncate flex-1 text-left text-blue-400/70 hover:text-blue-300 hover:underline"
                  >
                    {att.filename}
                  </button>
                  {att.fileSize && (
                    <span className="text-gray-500 shrink-0">{formatFileSize(att.fileSize)}</span>
                  )}
                  <button
                    onClick={() => onDeleteAttachment(att.id)}
                    className="text-gray-500 hover:text-red-400 shrink-0"
                    title="Remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[10px] text-gray-500 py-2 text-center border border-dashed border-gray-700 rounded">
              No files attached
            </div>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" size="sm" onClick={onCancel} className="text-xs h-7">Cancel</Button>
        <Button
          size="sm"
          className="text-xs h-7"
          disabled={!title || isPending}
          onClick={() => onSave({ title, text: text || null })}
        >
          {isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          Save
        </Button>
      </DialogFooter>
    </>
  );
}
